declare global {
    interface Window {
        grecaptcha: {
            enterprise: {
                execute: (siteKey: string, options: { action: string }) => Promise<string>;
            };
        };
    }
}

interface FormState {
    preferred_contact_method: "email" | "phone" | "sms" | "";
}

type FormErrors = Record<string, string>;

interface LaravelErrorResponse {
    errors: Record<string, string[]>;
}

export default function formHandler(formType: string = "contact", redirectUrl: string = "") {
    return {
        formType,
        redirectUrl,
        form: {
            preferred_contact_method: "",
        } as FormState,
        errors: {} as FormErrors,
        isSubmitting: false,
        submitStatus: null as "success" | "error" | null,
        submitMessage: "",

        init() {
            // Component initialization if needed
        },

        async submitForm() {
            // Reset previous submit status and errors
            this.submitStatus = null;
            this.submitMessage = "";
            this.errors = {};

            this.isSubmitting = true;

            try {
                // Execute reCAPTCHA Enterprise to obtain token
                const recaptchaToken = await window.grecaptcha.enterprise.execute(
                    window.RECAPTCHA_SITE_KEY,
                    { action: "submit_form" },
                );

                const response = await fetch(
                    `https://selig.sincore.com/api/v1/forms/${this.formType}/submit`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Accept: "application/json",
                        },
                        body: JSON.stringify({
                            ...this.form,
                            recaptcha_token: recaptchaToken,
                        }),
                    },
                );

                if (response.status === 422) {
                    // Handle Laravel validation errors
                    const errorData = (await response.json()) as LaravelErrorResponse;
                    if (errorData.errors && typeof errorData.errors === "object") {
                        // Map Laravel error bag format to errors object, taking first message per field
                        for (const [field, messages] of Object.entries(errorData.errors)) {
                            if (Array.isArray(messages) && messages.length > 0) {
                                this.errors[field] = messages[0];
                            }
                        }
                    }
                    this.submitStatus = "error";
                    this.submitMessage = "Please correct the errors below.";
                    this.$nextTick(() => {
                        (this.$refs.formStatus as HTMLElement)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    });
                    return;
                }

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.message || `Server error: ${response.status}`);
                }

                if (this.redirectUrl) {
                    window.location.href = this.redirectUrl;
                } else {
                    this.submitStatus = "success";
                    this.submitMessage = "Thank you for contacting us. We will be in touch soon.";
                }
            } catch (error) {
                this.submitStatus = "error";
                this.submitMessage =
                    error instanceof Error ? error.message : "An error occurred. Please try again.";
                this.$nextTick(() => {
                    (this.$refs.formStatus as HTMLElement)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                });
            } finally {
                this.isSubmitting = false;
            }
        },

        onPreferredMethodChange() {
            // Clear errors for toggled fields when contact method changes
            if (this.form.preferred_contact_method === "email") {
                delete this.errors.phone_number;
            } else if (
                this.form.preferred_contact_method === "phone" ||
                this.form.preferred_contact_method === "sms"
            ) {
                delete this.errors.email;
            }
        },
    };
}
